import Close from "@mui/icons-material/Close";
import { Modal } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { providerProfileState } from "../../recoil/atoms";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { generateProviderLetter } from "./Services/UserReportService";
import {
  ProviderLetterButton,
  ProviderLetterCheckbox,
  ProviderLetterCheckboxLabel,
  ProviderLetterCheckboxes,
  ProviderLetterCloseButton,
  ProviderLetterDocument,
  ProviderLetterModalContent,
  ProviderLetterModalWrapper,
  UserReportGenerateNotesButtonsWrapper,
  UserReportGenerateNotesModalNPIMessage,
  UserReportGenerateNotesTitle,
} from "./UserReport.styles";
import { getProviderLetter } from "./helpers";

type ProviderLetterStep = "review" | "submitted";

export const ProviderLetterModal: React.FC<{
  generateNotesModalOpen: boolean;
  setGenerateNotesModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCheck: React.Dispatch<React.SetStateAction<boolean | undefined>>;
}> = ({ generateNotesModalOpen, setGenerateNotesModalOpen, setCheck }) => {
  const { physicalDeviceType } = useDeviceHook();
  const { auth, setLoggedOut, setProviderAuth } = useAuthenticationHook();
  const providerProfile = useRecoilValue(providerProfileState);
  const [step, setStep] = useState<ProviderLetterStep>("review");
  const [letterReviewed, setLetterReviewed] = useState(false);
  const [willingToSpeakWithLegal, setWillingToSpeakWithLegal] = useState(false);
  const [loading, setLoading] = useState(false);
  const providerName = auth?.default_provider_info?.default_provider_name ?? "";
  const npi = auth?.default_provider_info?.default_npi ?? "";
  const practice = providerProfile?.account.sfname;
  const city = providerProfile?.account.billingcity;
  const state =
    providerProfile?.account.billingstatecode ||
    providerProfile?.account.billingstate;
  const location = [city, state].filter(Boolean).join(", ");

  useEffect(() => {
    if (generateNotesModalOpen) {
      setStep("review");
      setLetterReviewed(false);
      setWillingToSpeakWithLegal(false);
      setLoading(false);
    }
  }, [generateNotesModalOpen]);

  const closeModal = () => {
    setGenerateNotesModalOpen(false);
    setStep("review");
    setLetterReviewed(false);
    setWillingToSpeakWithLegal(false);
    setLoading(false);
  };

  const submitLetter = async () => {
    if (!auth?.token || !letterReviewed) return;

    const signatureLines = [
      "Signed and submitted by ordering provider:",
      npi ? `${providerName} (NPI: ${npi})` : providerName,
    ];

    if (practice) signatureLines.push(`Practice: ${practice}`);
    if (location) signatureLines.push(`City, State: ${location}`);

    const letterBody = `${getProviderLetter()}\n\n${signatureLines.join("\n")}`;

    setLoading(true);
    const response = await generateProviderLetter(
      letterBody,
      providerName,
      willingToSpeakWithLegal,
      auth.token,
      setLoggedOut,
    );
    setLoading(false);

    if (response) {
      setCheck(false);
      setProviderAuth((currentAuth) =>
        currentAuth
          ? { ...currentAuth, show_client_warning: false }
          : currentAuth,
      );
      setLetterReviewed(false);
      setWillingToSpeakWithLegal(false);
      setStep("submitted");
    }
  };

  return (
    <Modal
      open={generateNotesModalOpen}
      disableEscapeKeyDown
      style={{
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <ProviderLetterModalWrapper
        deviceType={physicalDeviceType}
        style={{ width: step === "submitted" ? "fit-content" : undefined }}
      >
        <UserReportGenerateNotesTitle
          deviceType={physicalDeviceType}
          style={{
            width: step === "submitted" ? "auto" : undefined,
            gap: step === "submitted" ? "10px" : undefined,
          }}
        >
          <div>Protect Patient Access to UNITY Testing</div>
          <ProviderLetterCloseButton
            aria-label="Close provider advocacy modal"
            onClick={closeModal}
          >
            <Close />
          </ProviderLetterCloseButton>
        </UserReportGenerateNotesTitle>

        {step === "review" && (
          <ProviderLetterModalContent>
            <p>
              Some practices have been affected by Labcorp&apos;s decision to
              discontinue courtesy blood draws for Unity testing, creating
              unnecessary barriers to patient access and interfering with
              providers&apos; ability to order the testing they determine is
              most appropriate for their patients.
            </p>
            <p>
              <strong>
                If this has affected your practice, we invite you to add your
                name to a petition to support patient access and provider
                choice.
              </strong>
            </p>
            <ProviderLetterDocument>
              {getProviderLetter()
                .split("\n")
                .map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
            </ProviderLetterDocument>
            <ProviderLetterCheckboxes>
              <ProviderLetterCheckboxLabel
                control={
                  <ProviderLetterCheckbox
                    checked={letterReviewed}
                    onChange={(event) =>
                      setLetterReviewed(event.target.checked)
                    }
                  />
                }
                label="I have reviewed this letter and would like to add my name."
              />
              <div style={{ marginLeft: "30px" }}>
                <div>{providerName}</div>
                {practice && <div>{practice}</div>}
                {location && <div>{location}</div>}
              </div>
              <ProviderLetterCheckboxLabel
                control={
                  <ProviderLetterCheckbox
                    checked={willingToSpeakWithLegal}
                    onChange={(event) =>
                      setWillingToSpeakWithLegal(event.target.checked)
                    }
                  />
                }
                label="I am willing to speak with BillionToOne's legal team if additional information about my experience would be helpful."
              />
            </ProviderLetterCheckboxes>
            <UserReportGenerateNotesButtonsWrapper
              style={{ justifyContent: "flex-end", margin: 0 }}
            >
              <ProviderLetterButton
                label="SUBMIT"
                onClick={submitLetter}
                disabled={!letterReviewed}
                loading={loading}
                fullWidth
              />
            </UserReportGenerateNotesButtonsWrapper>
          </ProviderLetterModalContent>
        )}

        {step === "submitted" && (
          <>
            <UserReportGenerateNotesModalNPIMessage
              style={{
                margin: "20px 20px 0",
                textAlign: "center",
                fontSize: "16px",
              }}
            >
              Thank you for protecting access to UNITY!
            </UserReportGenerateNotesModalNPIMessage>
            <UserReportGenerateNotesButtonsWrapper
              style={{ justifyContent: "flex-end" }}
            >
              <ProviderLetterButton
                label="DONE"
                onClick={closeModal}
                fullWidth
              />
            </UserReportGenerateNotesButtonsWrapper>
          </>
        )}
      </ProviderLetterModalWrapper>
    </Modal>
  );
};
